<template>
  <section class="space-y-4">
    <div class="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
      <div class="grid grid-cols-3">
        <button
          v-for="section in sections"
          :key="section.key"
          type="button"
          class="relative h-12 px-2 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors"
          :class="activeSection === section.key ? 'text-blue-950' : 'text-gray-600 hover:text-blue-700'"
          @click="activeSection = section.key"
        >
          <span class="truncate">{{ section.label }}</span>
          <span
            v-if="section.count"
            class="h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-xs flex items-center justify-center"
          >
            {{ section.count }}
          </span>
          <span
            v-if="activeSection === section.key"
            class="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-700"
          ></span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex flex-col items-center justify-center py-12">
      <div class="w-9 h-9 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
      <p class="mt-3 text-sm text-blue-800">Loading network...</p>
    </div>

    <div v-else-if="!activeUsers.length" class="bg-white rounded-lg border border-gray-100 p-8 text-center">
      <span class="material-symbols-outlined text-4xl text-gray-300">{{ emptyIcon }}</span>
      <p class="mt-2 text-gray-700 font-medium">{{ emptyTitle }}</p>
      <p class="text-sm text-gray-500 mt-1">{{ emptyCopy }}</p>
      <button
        v-if="activeSection === 'connections'"
        type="button"
        class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        @click="$emit('go-discover')"
      >
        Find Peers
      </button>
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      <peer-profile-card
        v-for="user in activeUsers"
        :key="user.id"
        :user="user"
        :connection-status="getConnectionStatus(user.id)"
        :show-reject="activeSection === 'requests'"
        @connect="handleCardAction"
        @reject="$emit('reject-request', $event)"
        @chat="$emit('chat', $event)"
        @view="$emit('view', $event)"
      />
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import PeerProfileCard from './PeerProfileCard.vue';

export default {
  name: 'NetworkPanel',
  components: { PeerProfileCard },
  props: {
    connections: {
      type: Array,
      default: () => []
    },
    pendingConnections: {
      type: Array,
      default: () => []
    },
    connectionRequests: {
      type: Array,
      default: () => []
    },
    getConnectionStatus: {
      type: Function,
      required: true
    }
  },
  emits: [
    'accept-request',
    'cancel-request',
    'remove-connection',
    'go-discover',
    'chat',
    'view'
  ],
  data() {
    return {
      activeSection: 'connections',
      loading: false
    };
  },
  computed: {
    ...mapState(['socialUserDetailsById']),
    sections() {
      return [
        { key: 'connections', label: 'Connected', count: this.connections.length },
        { key: 'requests', label: 'Requests', count: this.connectionRequests.length },
        { key: 'pending', label: 'Pending', count: this.pendingConnections.length }
      ];
    },
    activeIds() {
      if (this.activeSection === 'requests') return this.connectionRequests;
      if (this.activeSection === 'pending') return this.pendingConnections;
      return this.connections;
    },
    activeUsers() {
      return this.activeIds
        .map(userId => this.socialUserDetailsById[userId])
        .filter(Boolean);
    },
    emptyIcon() {
      if (this.activeSection === 'requests') return 'mark_email_unread';
      if (this.activeSection === 'pending') return 'pending_actions';
      return 'person_search';
    },
    emptyTitle() {
      if (this.activeSection === 'requests') return 'No connection requests';
      if (this.activeSection === 'pending') return 'No pending requests';
      return 'No connections yet';
    },
    emptyCopy() {
      if (this.activeSection === 'requests') return 'Requests from other students will appear here.';
      if (this.activeSection === 'pending') return 'Requests you send will appear here until accepted.';
      return 'Discover peers and start building your academic network.';
    }
  },
  watch: {
    activeSection: {
      immediate: true,
      handler() {
        this.fetchActiveDetails();
      }
    },
    activeIds() {
      this.fetchActiveDetails();
    }
  },
  methods: {
    async fetchActiveDetails() {
      if (!this.activeIds.length) return;
      this.loading = true;
      try {
        await this.$store.dispatch('fetchSocialUserDetails', this.activeIds);
      } finally {
        this.loading = false;
      }
    },
    handleCardAction(userId) {
      const status = this.getConnectionStatus(userId);
      if (status === 'connected') {
        this.$emit('remove-connection', userId);
      } else if (status === 'request') {
        this.$emit('accept-request', userId);
      } else if (status === 'pending') {
        this.$emit('cancel-request', userId);
      }
    }
  }
};
</script>
